<template>
    <CDropdown ref="dropdown" class="w-full" filter :disabled="!!$attrs.disabled">
        <template #trigger="{ isOpen }">
            <div
                class="input flex h-10 cursor-pointer select-none items-center justify-between gap-3"
                :class="{ 'input--disabled': $attrs.disabled }"
            >
                <span
                    class="min-w-0 flex-1 truncate"
                    :class="selectedItemsLabel ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-txt-body'"
                >
                    {{ selectedItemsLabel || $attrs.placeholder }}
                </span>
                <CIcon :name="isOpen ? 'dropdown-up' : 'dropdown-down'" class="size-5 shrink-0 text-gray-400 dark:text-txt-body" />
            </div>
        </template>

        <template #content>
            <slot name="dropdown-header" />

            <div v-if="!hideOptions" class="min-w-64">
                <CFormInput
                    v-if="searchable"
                    v-model="searchQuery"
                    class="mb-4 w-full"
                    placeholder="Vyhľadať…"
                >
                    <template v-if="searchQuery" #suffix>
                        <CIcon class="hover:100 size-6 cursor-pointer opacity-75" name="close" @click="clear()" />
                    </template>
                </CFormInput>

                <div class="max-h-96 overflow-y-auto">
                    <CFormCheckboxGroup
                        v-if="filteredOptions?.length"
                        :model-value="currentValue"
                        :options="filteredOptions"
                        option-label-key="label"
                        option-value-key="id"
                        option-disabled-key="disabled"
                        @update:model-value="handleUpdate"
                    />

                    <template v-if="filteredGroupedOptions?.length">
                        <template v-for="(group, idx) in filteredGroupedOptions">
                            <div v-if="group?.length" :key="idx" class="flex flex-col">
                                <CLineBreak
                                    v-if="
                                        (!idx && filteredOptions?.length) ||
                                        (idx && filteredGroupedOptions?.[idx - 1]?.length)
                                    "
                                    class="my-4!"
                                />

                                <CFormCheckboxGroup
                                    :model-value="currentValue"
                                    :options="group"
                                    option-label-key="label"
                                    option-value-key="id"
                                    option-disabled-key="disabled"
                                    @update:model-value="handleUpdate"
                                />
                            </div>
                        </template>
                    </template>
                </div>
            </div>

            <slot name="dropdown-footer" />
        </template>
    </CDropdown>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { matchSorter } from 'match-sorter'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
    value?: (string | number)[]
    modelValue?: (string | number)[]
    options: any[]
    groupedOptions?: any[][]
    alterLabel?: (label: string) => string
    hideOptions?: boolean
    searchable?: boolean
}>()

const emit = defineEmits<{
    (e: 'input', value: (string | number)[]): void
    (e: 'update:model-value', value: (string | number)[]): void
}>()

const dropdown = ref()
const searchQuery = ref('')

// Computed property to handle both value and modelValue
const currentValue = computed(() => {
    const value = props.modelValue || props.value
    return Array.isArray(value) ? value : []
})

const selectedItemsLabel = computed(() => {
    const label = [...(props.options || []), ...(props.groupedOptions?.flat() || [])]
        .filter((item: any) => currentValue.value.includes(item.id))
        .map((item: any) => item.label)
        .join(', ')

    return typeof props.alterLabel === 'function' ? props.alterLabel(label) : label
})

const filteredOptions = computed(() => {
    if (!searchQuery.value) {
        return props.options
    }

    return matchSorter(props.options, searchQuery.value, {
        keys: ['label']
    })
})

const filteredGroupedOptions = computed(() => {
    if (!searchQuery.value) {
        return props.groupedOptions
    }

    return props.groupedOptions?.map((options: any) =>
        matchSorter(options, searchQuery.value, {
            keys: ['label']
        })
    )
})

function clear() {
    searchQuery.value = ''
}

// Handle update to emit both events
function handleUpdate(value: (string | number)[]) {
    emit('input', value)
    emit('update:model-value', value)
}

// Watch for hideOptions changes
watch(
    () => props.hideOptions,
    (value) => {
        if (value) {
            handleUpdate([])
        }
    }
)
</script>
